import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import axios from 'axios';
import { Tags } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { marketApi } from '@/services/marketApi';
import type {
  MarketApplicationTarget,
  MarketCategory,
  MarketItemType,
  MarketType,
  ProtoInt64,
} from '@/types';

interface CategoryFormValues {
  categoryId: string;
  displayNameZh: string;
  displayNameEn: string;
  sortOrder: number;
  enabled: boolean;
}

const itemTypeOptions = [
  { label: '连接器（connector）', value: 'connector' },
  { label: '技能（skill）', value: 'skill' },
];

const marketTypeOptions = [
  { label: '海外市场计数', value: 'overseas' },
  { label: '国内市场计数', value: 'domestic' },
];

function errorMessage(error: unknown) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string; reason?: string }
      | undefined;
    if (data?.reason === 'MARKET_CONFLICT') {
      return '分类已被其他管理员更新，请刷新后重试';
    }
    return data?.message || data?.reason || error.message;
  }
  return error instanceof Error ? error.message : '分类保存失败';
}

function numberValue(value: ProtoInt64) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function CategoryManager({
  target,
  fixedItemType,
}: {
  target: MarketApplicationTarget;
  fixedItemType?: 'skill';
}) {
  const [form] = Form.useForm<CategoryFormValues>();
  const [open, setOpen] = useState(false);
  const [itemType, setItemType] = useState<MarketItemType>(
    fixedItemType ?? 'connector',
  );
  const [marketType, setMarketType] = useState<MarketType>('overseas');
  const [categories, setCategories] = useState<MarketCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<MarketCategory>();
  const [saving, setSaving] = useState(false);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await marketApi.listCategories(target, {
        itemType,
        marketType,
        includeDisabled: true,
      });
      setCategories(
        (response.categories ?? [])
          .filter((category) => category.kind === 'category')
          .sort(
            (left, right) =>
              left.sortOrder - right.sortOrder ||
              left.categoryId.localeCompare(right.categoryId),
          ),
      );
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [itemType, marketType, target]);

  useEffect(() => {
    if (open) {
      void loadCategories();
    }
  }, [loadCategories, open]);

  const openEditor = useCallback(
    (category?: MarketCategory) => {
      setEditing(category);
      setEditorOpen(true);
      form.setFieldsValue({
        categoryId: category?.categoryId ?? '',
        displayNameZh: category?.displayNameZh ?? '',
        displayNameEn: category?.displayNameEn ?? '',
        sortOrder: category?.sortOrder ?? 80,
        enabled: category?.enabled ?? true,
      });
    },
    [form],
  );

  const saveCategory = useCallback(async () => {
    let values: CategoryFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      await marketApi.upsertCategory(target, {
        itemType,
        categoryId: values.categoryId.trim(),
        displayNameZh: values.displayNameZh.trim(),
        displayNameEn: values.displayNameEn.trim(),
        sortOrder: values.sortOrder,
        enabled: values.enabled,
        expectedLockVersion: editing?.persisted ? editing.lockVersion : 0,
      });
      message.success(editing ? '分类已更新' : '分类已创建');
      setEditorOpen(false);
      setEditing(undefined);
      form.resetFields();
      await loadCategories();
    } catch (error) {
      message.error(errorMessage(error));
      await loadCategories();
    } finally {
      setSaving(false);
    }
  }, [editing, form, itemType, loadCategories, target]);

  const columns: ColumnsType<MarketCategory> = useMemo(
    () => [
      {
        title: '分类标识',
        dataIndex: 'categoryId',
        render: (value: string, category) => (
          <Space size={6}>
            <Typography.Text code>{value}</Typography.Text>
            {!category.persisted ? <Tag>系统默认</Tag> : null}
          </Space>
        ),
      },
      {
        title: '名称',
        key: 'name',
        render: (_, category) => (
          <div>
            <div>{category.displayNameZh}</div>
            <Typography.Text type="secondary">
              {category.displayNameEn}
            </Typography.Text>
          </div>
        ),
      },
      { title: '排序', dataIndex: 'sortOrder', width: 80 },
      {
        title: `${marketType === 'overseas' ? '海外' : '国内'}条目`,
        dataIndex: 'itemCount',
        width: 100,
        render: numberValue,
      },
      {
        title: '状态',
        dataIndex: 'enabled',
        width: 90,
        render: (enabled: boolean) => (
          <Tag color={enabled ? 'green' : undefined}>
            {enabled ? '启用' : '停用'}
          </Tag>
        ),
      },
      {
        title: '操作',
        key: 'actions',
        width: 80,
        render: (_, category) => (
          <Button type="link" onClick={() => openEditor(category)}>
            编辑
          </Button>
        ),
      },
    ],
    [marketType, openEditor],
  );

  return (
    <>
      <Button icon={<Tags className="h-4 w-4" />} onClick={() => setOpen(true)}>
        分类管理
      </Button>
      <Drawer
        destroyOnHidden
        title="Market 分类管理"
        open={open}
        width={900}
        onClose={() => setOpen(false)}
      >
        <Alert
          className="mb-4"
          showIcon
          type="info"
          message="分类按条目类型独立维护"
          description="connector 与 skill 可以使用同名分类标识，但配置互不影响。市场选项只切换条目计数，分类定义对国内、海外共用。"
        />
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <Space wrap>
            <Select
              className="w-[210px]"
              options={itemTypeOptions}
              disabled={Boolean(fixedItemType)}
              value={itemType}
              onChange={setItemType}
            />
            <Select
              className="w-[160px]"
              options={marketTypeOptions}
              value={marketType}
              onChange={setMarketType}
            />
          </Space>
          <Space>
            <Button loading={loading} onClick={() => void loadCategories()}>
              刷新
            </Button>
            <Button type="primary" onClick={() => openEditor()}>
              新增分类
            </Button>
          </Space>
        </div>
        <Table
          columns={columns}
          dataSource={categories}
          loading={loading}
          pagination={false}
          rowKey={(category) => `${category.itemType}:${category.categoryId}`}
        />
      </Drawer>

      <Modal
        destroyOnHidden
        title={`${editing ? '编辑' : '新增'} ${itemType} 分类`}
        open={editorOpen}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        onCancel={() => {
          setEditorOpen(false);
          setEditing(undefined);
          form.resetFields();
        }}
        onOk={() => void saveCategory()}
      >
        <Form<CategoryFormValues>
          form={form}
          layout="vertical"
          preserve={false}
        >
          <Form.Item
            name="categoryId"
            label="分类标识"
            extra="仅在当前条目类型内唯一，创建后不可修改"
            rules={[
              { required: true, message: '请输入分类标识' },
              {
                pattern: /^[a-z0-9][a-z0-9-]{0,63}$/,
                message: '使用 1–64 位小写字母、数字或连字符',
              },
            ]}
          >
            <Input disabled={Boolean(editing)} placeholder="例如 research" />
          </Form.Item>
          <Form.Item
            name="displayNameZh"
            label="中文名称"
            rules={[{ required: true, message: '请输入中文名称' }]}
          >
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item
            name="displayNameEn"
            label="英文名称"
            rules={[{ required: true, message: '请输入英文名称' }]}
          >
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item
            name="sortOrder"
            label="排序"
            rules={[{ required: true, message: '请输入排序' }]}
          >
            <InputNumber className="w-full" min={0} precision={0} />
          </Form.Item>
          <Form.Item name="enabled" label="启用状态" valuePropName="checked">
            <Switch
              checkedChildren="启用"
              unCheckedChildren="停用"
              disabled={editing?.categoryId === 'other'}
            />
          </Form.Item>
          {editing && numberValue(editing.itemCount) > 0 ? (
            <Alert
              showIcon
              type="warning"
              message="仍有条目使用该分类时，服务端会拒绝停用"
            />
          ) : null}
        </Form>
      </Modal>
    </>
  );
}
